import classNames from 'classnames'
import { useCallback, useEffect, useRef, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'

import { deleteParamsFromUrl, getUrlWithParams } from 'core-components/helpers/url'

import styles from '../Accordion.module.scss'
import {
  IAccordionGroupProps,
  IAccordionGroupTab,
  IAccordionGroupToggleViewProps,
} from '../models/IAccordionGroupProps'

interface IToggleProps extends IAccordionGroupToggleViewProps {
  className: IAccordionGroupProps['className']
  visibleItems: IAccordionGroupTab[]
  defaultItem: IAccordionGroupProps['defaultItem']
  urlKey: IAccordionGroupProps['urlKey']
}

export const Toggle = ({
  visibleItems,
  margin,
  className,
  defaultItem,
  headerClassName,
  urlKey,
  contentAlwaysExisting,
  noPadding,
  bodyClassName,
  iconOpened,
  icon,
}: IToggleProps) => {
  const [currentTabId, setCurrentTabId] = useState<string | undefined>(defaultItem)
  const navigate = useNavigate()
  const isInitCurrentTabSet = useRef<boolean>(false)

  const calcToLink = useCallback(
    (item: IAccordionGroupTab) => {
      if (urlKey) {
        if (currentTabId === item.id) {
          return deleteParamsFromUrl([urlKey])
        }
        return getUrlWithParams({ [urlKey]: item.id })
      }
      return ''
    },
    [currentTabId, urlKey]
  )

  const onTabChange = useCallback((itemId: string) => {
    setCurrentTabId((prev) => {
      if (prev === itemId) {
        return undefined
      }
      return itemId
    })
  }, [])

  const getContent = useCallback(
    (item: IAccordionGroupTab) => {
      if (contentAlwaysExisting) {
        return (
          <div
            data-testid={`accordion_group_toggle_body-${item.id}`}
            className={classNames(
              styles.body,
              noPadding ? styles['body--no-padding'] : '',
              item.id !== currentTabId && styles['body--hidden'],
              bodyClassName ? bodyClassName : ''
            )}
          >
            <>{item.content()}</>
          </div>
        )
      }

      return item.id === currentTabId ? (
        <div
          data-testid={`accordion_group_toggle_body-${item.id}`}
          className={classNames(
            styles.body,
            noPadding ? styles['body--no-padding'] : '',
            bodyClassName ? bodyClassName : ''
          )}
        >
          <>{item.content()}</>
        </div>
      ) : null
    },
    [bodyClassName, contentAlwaysExisting, currentTabId, noPadding]
  )

  useEffect(() => {
    if (urlKey && defaultItem && !isInitCurrentTabSet.current) {
      navigate(getUrlWithParams({ [urlKey]: defaultItem }))
      setCurrentTabId(defaultItem)
      isInitCurrentTabSet.current = true
    }
  }, [defaultItem, navigate, urlKey])

  return (
    <>
      {visibleItems.map((item, index) => {
        return (
          <div
            key={index}
            data-testid={`accordion_group_toggle_root-${item.id}`}
            className={classNames(
              styles.root,
              index < visibleItems.length - 1 ? styles.groupItem : '',
              { [styles[`margin-${margin}`]]: margin },
              className || ''
            )}
          >
            <Link
              className={classNames(
                styles.trigger,
                currentTabId ? styles['trigger--opened'] : '',
                headerClassName || ''
              )}
              onClick={() => onTabChange(item.id)}
              to={calcToLink(item)}
              key={`tabButton-${item.id}`}
              data-testid={`accordion_group_horizontal_item-${item.id}`}
            >
              {iconOpened && currentTabId === item.id
                ? iconOpened
                : icon || (
                    <div
                      className={classNames(
                        'kbase kbase-close-circle',
                        styles.icon,
                        currentTabId === item.id ? '' : styles['icon--collapsed']
                      )}
                    />
                  )}
              {item.title && item.title()}
            </Link>
            {getContent(item)}
          </div>
        )
      })}
    </>
  )
}
